import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Logo}.
 *
 * All sizing classes resolve to design tokens from @atlas/tokens (brand).
 * No hardcoded values.
 *
 * @remarks
 * - Supports four sizes: sm (h-6), md (h-8, default), lg (h-10), xl (h-12)
 * - The Keenfolks mark, colored by the surrounding text token
 *
 * @example
 * ```tsx
 * const classes = logoVariants({ size: 'md' })
 * // Returns: "inline-flex h-8"
 * ```
 */
export const logoVariants = cva('inline-flex', {
	variants: {
		size: {
			sm: 'h-6',
			md: 'h-8',
			lg: 'h-10',
			xl: 'h-12',
		},
	},
	defaultVariants: { size: 'md' },
})

/**
 * Props for the {@link Logo} component.
 *
 * @property size - Logo size: 'sm' (h-6) | 'md' (h-8, default) | 'lg' (h-10) | 'xl' (h-12)
 * @property className - Optional additional Tailwind classes
 *
 * @example
 * ```tsx
 * <Logo size="lg" />
 * ```
 */
export interface LogoProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof logoVariants> {}

/**
 * Logo component displaying brand logo.
 *
 * @remarks
 * - **Styling:** All sizing uses design tokens from `@atlas/tokens` (brand).
 *   No hardcoded values.
 * - **Sizes:** Small (h-6), medium (h-8, default), large (h-10), xl (h-12).
 * - **Logo:** the Keenfolks K mark (currentColor).
 * - **Usage:** Application header, navigation, footer.
 *
 * @example Basic usage
 * ```tsx
 * import { Logo } from '@atlas/ui-primitives'
 *
 * function Header() {
 *   return (
 *     <header>
 *       <Logo size="lg" />
 *     </header>
 *   )
 * }
 * ```
 *
 * @example With different sizes
 * ```tsx
 * <Logo size="sm" />
 * <Logo size="md" />
 * <Logo size="lg" />
 * <Logo size="xl" />
 * ```
 *
 * @see {@link logoVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/logo/logo.stories.tsx for visual examples
 */
export function Logo({ size, className, ...props }: LogoProps) {
	return (
		<div {...props} className={cn(logoVariants({ size }), className)}>
			<svg viewBox="0 0 256 256" fill="currentColor" className="h-full w-auto" aria-hidden="true">
				<path d="M14,16 H58 V144 L14,188 Z" />
				<path d="M196,16 H240 V78 L78,240 H14 L196,58 Z" />
				<path d="M241,240 H196 V184 L133,121 L165,89 L241,164 Z" />
			</svg>
		</div>
	)
}
